import {
  Alert,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import FormTextField from "../components/formComponents/FormTextField";
import { useAuthContext } from "../auth/AuthContext";
import type { ResolvedOrderProduct } from "./useOrderBookings";
import { useOrderBookings } from "./useOrderBookings";
import dayjs from "../dayjs";
import FileDropZone from "./FileDropZone";
import FormSwitch from "../components/formComponents/FormSwitch";

const PageTitle: FC<{ readonly orderId: number; readonly orderName: string }> = ({ orderId, orderName }) => (
  <Typography variant="h5">
    Rezerwacja dla zamówienia {orderId} ({orderName})
  </Typography>
);

const ProductDetails: FC<{ products: ResolvedOrderProduct[] }> = ({ products }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Produkt</TableCell>
          <TableCell>Data</TableCell>
          <TableCell>Opcja</TableCell>
          <TableCell>Liczba sztuk</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {products.flatMap((product) =>
          product.options.map((option, oIdx) => (
            <TableRow key={`${product.productId}-${option.id}`}>
              {oIdx === 0 && <TableCell rowSpan={product.options.length}>{product.name}</TableCell>}
              {oIdx === 0 && (
                <TableCell rowSpan={product.options.length}>{dayjs(product.date).format("DD.MM.YYYY")}</TableCell>
              )}
              <TableCell>{option.name}</TableCell>
              <TableCell>{option.quantity}</TableCell>
            </TableRow>
          )),
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const BookingAlreadyProvided: FC<{
  readonly orderId: number;
  readonly orderName: string;
  readonly products: ResolvedOrderProduct[];
}> = ({ orderId, orderName, products }) => {
  const bookingLines = products
    .map((p) => p.booking)
    .filter((b) => b !== undefined)
    .map((b) => `${b.bookingNumber} (${dayjs(b.createdAt).format("DD.MM.YYYY HH:mm")})`);
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid size={12}>
        <PageTitle orderId={orderId} orderName={orderName} />
      </Grid>
      <Grid size={12}>
        <ProductDetails products={products} />
      </Grid>
      <Grid size={12}>
        <Alert severity="info">
          Dane rezerwacji zostały już uzupełnione
          {bookingLines.map((line) => (
            <span key={line}>
              <br />
              {line}
            </span>
          ))}
        </Alert>
      </Grid>
    </Grid>
  );
};

interface BookingFormState {
  bookingNumber: string;
  note: string;
  attachment: File | null;
  productsUnavailable: boolean;
}

const BookingForm: FC<{
  readonly orderId: number;
  readonly orderName: string;
  readonly products: ResolvedOrderProduct[];
  readonly onSubmit: (bookingNumber: string, note?: string, attachment?: File) => Promise<void>;
}> = ({ orderId, orderName, products, onSubmit }) => {
  const { control, handleSubmit, watch } = useForm<BookingFormState>({
    defaultValues: { bookingNumber: "", note: "", attachment: null, productsUnavailable: false },
  });

  const productsUnavailable = watch("productsUnavailable");

  const submit = handleSubmit(async ({ note, bookingNumber, attachment, productsUnavailable: unavailable }) => {
    const effectiveBookingNumber = unavailable ? "UNAVAILABLE" : bookingNumber;
    await onSubmit(effectiveBookingNumber, note ? note : undefined, attachment ?? undefined);
  });

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid size={12}>
        <PageTitle orderId={orderId} orderName={orderName} />
      </Grid>
      <Grid size={12}>
        <Typography variant="h6">Szczegóły zamówienia</Typography>
      </Grid>
      <Grid size={12}>
        <ProductDetails products={products} />
      </Grid>
      <Grid size={12}>
        <Typography variant="h6">Dane rezerwacji</Typography>
      </Grid>
      <Grid container size={{ xs: 12, md: 8 }}>
        <Grid size={12}>
          <FormSwitch name="productsUnavailable" control={control} label="Produkty niedostępne" />
        </Grid>
        <Grid size={12}>
          <FormTextField
            name="bookingNumber"
            control={control}
            label="Numer rezerwacji"
            rules={{ required: !productsUnavailable }}
            disabled={productsUnavailable}
          />
        </Grid>
        <Grid size={12}>
          <FormTextField name="note" control={control} label="Uwagi" multiline minRows={3} />
        </Grid>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Controller
          name="attachment"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FileDropZone file={value} onFileSelected={onChange} label="Załącznik" fullHeight />
          )}
        />
      </Grid>
      <Grid
        container
        size={12}
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <AsyncLoadingButton onClick={submit}>Zapisz</AsyncLoadingButton>
      </Grid>
    </Grid>
  );
};

export const OrderBookingPage = () => {
  const { context } = useAuthContext();
  const { isLoading, isNotFound, order, hasBookings, fillBookingDetails } = useOrderBookings();

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (isNotFound) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Takie zamówienie nie istnieje</Typography>
      </Grid>
    );
  }

  const isProvider = context?.role === "PROVIDER";
  if (!isProvider || !order?.id || !order?.name || !order.products) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Brak dostępu</Typography>
      </Grid>
    );
  }

  if (hasBookings) {
    return <BookingAlreadyProvided orderId={order.id} orderName={order.name} products={order.products} />;
  }

  return (
    <BookingForm
      orderId={order.id}
      orderName={order.name}
      products={order.products}
      onSubmit={async (bookingNumber, note, attachment) => {
        await fillBookingDetails(bookingNumber, note, attachment);
      }}
    />
  );
};
